import React, { useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Mustache from 'mustache';
import { useIsFocused, useNavigationState } from '@react-navigation/native';
import { centralAPI } from '../../../services/central-api';
import TimelineWrapper from '../../../components/TimelineWrapper';
import PtzControls from '../../../components/PtzControls';
import VideoPlayer from '../../../components/VideoPlayer';
import MyAppText from '../../../components/MyAppText';
import { cameraService } from '../../../services/central-api/cameras';
import { translate } from '../../../services/translate';
import NewAnalysisModal from '../VideoAnalysis/NewAnalysisModal';
import MaintenanceRequestModal from '../MaintenanceRequest/MaintenanceRequestModal';
import DownloadModal from './DownloadModal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import 'react-calendar-timeline/lib/Timeline.css';
import getBestContrastColor from '../../../services/best-contrast-color';
import { useAuth } from '../../../context/Auth';
import { VideoPlayerControls } from '../../../components/VideoPlayerControls';
import { Tabs } from '../../../components/Tabs';
import OtherCamerasList from './OtherCamerasList';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import DropdownButton from '../../../components/DropdownButton';
import Card from '../../../components/Card';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';
import { openInNewTab } from '../../../services/navigation-assistant';

interface CameraPlayerComponentProps {
    navigation: RootNavigation;
    page: 'map' | 'watch';
    route: {
        params: {
            id: string;
        };
    };
}

export default function CameraPlayerComponent({ navigation, route, page }: CameraPlayerComponentProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [camera, setCamera] = useState<Camera>();
    const [selectedCameraId, setSelectedCameraId] = useState<string>('');
    const [videoPlayer, setVideoPlayer] = useState<VideoPlayer>();
    const [cursorTime, setCursorTime] = useState<number>(new Date().getTime());
    const [isLive, setIsLive] = useState(true);
    const [currentRecord, setCurrentRecord] = useState<CameraRecord>();
    const [currentTempStreamMoment, setCurrentTempStreamMoment] = useState<number>();
    const [watermark, setWatermark] = useState<string>('');
    const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
    const [isZeusModalVisible, setIsZeusModalVisible] = useState(false);
    const [isMaintenanceRequestModalVisible, setIsMaintenanceRequestModalVisible] = useState(false);
    const [emergency, setEmergency] = useState(false);
    const [userBearerToken, setUserBearerToken] = useState<string>();

    const [isPlaying, setIsPlaying] = useState(true);
    const [theaterMode, setTheaterMode] = useState(false);

    const index = useNavigationState(state => state);

    const isFocused = useIsFocused();
    const cameraId = route.params.id;
    const isEnabled = isFocused && !!authenticatedUser && !!cameraId;

    useEffect(() => {
        if (!isEnabled) {
            return;
        }
        setCamera(undefined);
        setCursorTime(new Date().getTime());
        setIsLive(true);
        setCurrentRecord(undefined);
        setWatermark(String(authenticatedUser!.id));
        setIsDownloadModalVisible(false);
        setIsZeusModalVisible(false);
        setTheaterMode(false);
        setIsPlaying(true);
    }, [cameraId, isFocused, authenticatedUser]);

    useQuery({
        queryKey: ['camera', cameraId],
        queryFn: async () => {
            const cameraRes = await cameraService.getCamera(cameraId!);
            setCamera(cameraRes);
            return cameraRes;
        },
        enabled: isEnabled,
        retry: true,
        retryDelay: 3000,
    });

    const { data: recordList } = useQuery({
        queryKey: ['timeline', cameraId],
        queryFn: async () => await cameraService.getTimeline(cameraId!),
        enabled: isEnabled,
        refetchIntervalInBackground: true,
        refetchInterval: (data, query) => {
            if (query.state.status === 'error') {
                return 5000;
            }
            return 60000;
        },
    });

    useQuery({
        queryKey: ['cameraPing', camera?.id],
        queryFn: async () => {
            if (camera?.type === 'ptz' && !camera.originalCamera && !camera.thirdPartyCamera) {
                await cameraService.watchingPtzCamera({ cameraId: camera.id });
            }
            await centralAPI.checkAuthentication();
            return null;
        },
        enabled: isEnabled && !!camera,
        refetchInterval: 30000,
    });

    useEffect(() => {
        if (!videoPlayer) {
            return;
        }
        if (isLive) {
            setIsPlaying(true);
        }
    }, [videoPlayer, isLive]);

    useEffect(() => {
        if (!videoPlayer) {
            return;
        }

        if (!isFocused) {
            videoPlayer.destroyPlayers();
        }
    }, [videoPlayer, isFocused]);

    useEffect(() => {
        if (!videoPlayer) {
            return;
        }
        videoPlayer.setPlaying(isPlaying);
    }, [videoPlayer, isPlaying]);

    const videoPlayerRef = React.useCallback((videoPlayer: VideoPlayer) => {
        setVideoPlayer(videoPlayer);
    }, []);

    useEffect(() => {
        (async () => {
            const token = await centralAPI.getToken();
            if (token) {
                setUserBearerToken(token);
            }
        })();
    }, []);

    function loadLiveStream(camera: Camera, videoPlayer: VideoPlayer) {

        if (camera.storageType === 'card') {
            return videoPlayer.setSrc({
                srcObject: {
                    type: 'flv',
                    tempStream: true,
                    getTempFlvSrc: async () => {
                        const live = await cameraService.cardCameraFlvStream(camera!.id);
                        return { src: live.flvUrl };
                    },
                },
            });
        }

        if (!camera.streamUrl) {
            return;
        }

        if (camera.thirdPartyCamera?.type === 'hls') {
            return videoPlayer.setSrc({
                srcObject: {
                    type: 'hls',
                    src: camera.streamUrl,
                }
            });
        }

        videoPlayer.setSrc({
            srcObject: {
                type: 'flv',
                tempStream: false,
                src: camera.streamUrl,
            },
        });
    }

    useEffect(() => {
        (() => {
            try {
                if (!camera) {
                    return;
                }

                if (!videoPlayer) {
                    return;
                }

                loadLiveStream(camera, videoPlayer);

            } catch (err) {
                console.error(err);
            }
        })();

        return () => {
            videoPlayer?.componentWillUnmount();
        };
    }, [camera, videoPlayer]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const now = new Date();
                if (isLive) {
                    setCursorTime(now.getTime());
                } else {
                    if (videoPlayer && currentRecord) {
                        const time = videoPlayer.getTime();

                        const initialTime = currentTempStreamMoment ?? currentRecord.start;
                        // TODO: calcular milisegundos usando o moment
                        const date = new Date((time * 1000) + initialTime);
                        setCursorTime(date.getTime());
                    }
                }
            } catch (err) {
                console.error(err);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [videoPlayer, currentRecord, isLive, currentTempStreamMoment]);

    async function setTimeLineAt(moment: number) {
        if (!videoPlayer) {
            return;
        }

        setIsLive(false);

        if (!recordList) {
            return;
        }

        const cameraRecord = recordList.videos.findLast((record: CameraRecord) => {
            return record.finish > moment && record.start <= moment;
        });

        if (!cameraRecord || !camera) {
            return;
        }

        setCursorTime(moment);


        if (cameraRecord !== currentRecord || camera.storageType === 'card') {
            setCurrentRecord(cameraRecord);
            if (camera.storageType === 'card') {
                setCurrentTempStreamMoment(moment);
                videoPlayer.setSrc({
                    srcObject: {
                        type: 'flv',
                        tempStream: true,
                        videoDuration: cameraRecord.finish - moment,
                        getTempFlvSrc: async () => {
                            const recordedRtsp = await cameraService.cardCameraFlvStream(camera!.id, moment);
                            return { src: recordedRtsp.flvUrl };
                        },
                        onVideoEnded: async () => {
                            try {
                                const index = recordList.videos.indexOf(cameraRecord) + 1;

                                if (index < recordList.videos.length) {
                                    await setTimeLineAt(recordList.videos[index].start);
                                } else {

                                    setCursorTime(new Date().getTime());
                                    loadLiveStream(camera, videoPlayer);
                                    setCurrentRecord(undefined);
                                    setIsLive(true);
                                }
                            } catch (err) {
                                console.error(err);
                            }
                        }
                    },
                });
                return;
            }

            let videoURL;

            if (typeof recordList.videoTemplate === 'string') {
                videoURL = Mustache.render(recordList.videoTemplate, cameraRecord);
            } else if (recordList.videoTemplate && cameraRecord.videoStorageProviderId) {
                videoURL = Mustache.render(recordList.videoTemplate[cameraRecord.videoStorageProviderId], cameraRecord);
            }
            if (cameraRecord.videoStorageProviderId && (!camera.thirdPartyCamera || (camera.thirdPartyCamera && camera.originalCamera))) {
                const tokenResponse = await cameraService.getTimelineVideoSasToken(camera.id, cameraRecord.start, cameraRecord.finish, cameraRecord.videoStorageProviderId);
                videoURL += `?${tokenResponse.sasToken}`;
            }

            if (!videoURL) {
                return;
            }

            const srcUrl = new URL(videoURL);
            const newSrc = srcUrl.origin + srcUrl.pathname;
            let isMP4Video = false;
            try {
                isMP4Video = newSrc.endsWith('.mp4');
            } catch (error) {
                console.error('invalid URL for video source:', videoURL);
            }

            videoPlayer.setSrc({
                srcObject: {
                    type: isMP4Video ? 'mp4' : 'hls',
                    src: videoURL,
                    onVideoEnded: async () => {
                        try {
                            const index = recordList.videos.indexOf(cameraRecord) + 1;

                            if (index < recordList.videos.length) {
                                await setTimeLineAt(recordList.videos[index].start);
                            } else {

                                setCursorTime(new Date().getTime());
                                loadLiveStream(camera, videoPlayer);
                                setCurrentRecord(undefined);
                                setIsLive(true);
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }
                }
            });
        }

        videoPlayer.setTimeAt((moment - cameraRecord.start) / 1000);
    }

    function onRecordClick(moment: number) {
        setTimeLineAt(moment);
    }

    async function handleGoLive() {
        if (!videoPlayer) {
            return console.error('video not ready');
        }

        if (isLive) {
            return;
        }

        if (!camera) {
            return;
        }

        setCursorTime(new Date().getTime());
        loadLiveStream(camera, videoPlayer);
        setCurrentRecord(undefined);
        setIsLive(true);
    }

    function stepBack(multiplier: number) {
        if (!videoPlayer) {
            return;
        }

        onRecordClick(cursorTime - (multiplier * 1000));
    }

    function stepForward(multiplier: number) {
        if (!videoPlayer) {
            return;
        }

        onRecordClick(cursorTime + (multiplier * 1000));
    }

    function goBackAction() {
        if (index.routes.length > 1) {
            return navigation.goBack();
        }

        console.log('page', page);

        if (page === 'map') {
            navigation.navigate('Cameras', { screen: 'CamerasMap' });
            return;
        }
        if (page === 'watch') {
            navigation.navigate('Cameras', { screen: 'CamerasList' });
            return;
        }

    }

    useEffect(() => {
        if (selectedCameraId) {
            navigation.navigate('Cameras', { screen: 'CameraPlayer', params: { id: selectedCameraId } });
        }
    }, [selectedCameraId]);

    useEffect(() => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    }, [theaterMode]);

    return (
        <Container style={{ padding: 0 }} onLayout={() => window.dispatchEvent(new Event('resize'))}>
            <Header
                goBackAction={goBackAction}
                title={translate('camera')}
                actions={<DropdownButton
                    viewStyle={{ height: '100%' }}
                    isButton
                    actions={!camera ? [] : [{
                        content: translate('openCameraConfigurationOnNewTab'),
                        onPress: () => {
                            openInNewTab('CRM', { screen: 'ManagementDetails', params: { id: camera.id } });
                        },
                        omit: !authenticatedUser?.isAdmin && !authenticatedUser?.permissions.crm_module_access,
                    }, {
                        content: translate('downloadVideo'),
                        onPress: () => setIsDownloadModalVisible(true),
                        omit: !authenticatedUser?.isAdmin && !authenticatedUser?.permissions.camera_download_video,
                    }, {
                        content: translate('requestAnalysis'),
                        onPress: () => setIsZeusModalVisible(true),
                        omit: Boolean(camera.thirdPartyCamera) || (!authenticatedUser?.isAdmin && !authenticatedUser?.permissions.camera_video_analysis),
                    }, {
                        content: translate('requestMaintenance'),
                        onPress: () => {
                            setEmergency(false);
                            setIsMaintenanceRequestModalVisible(true);
                        },
                        omit: Boolean(camera.thirdPartyCamera) || Boolean(camera.originalCamera) || (!authenticatedUser?.isAdmin && !authenticatedUser?.permissions.create_camera_maintenance_requests),
                    }, {
                        content: translate('requestEmergencyMaintenance'),
                        onPress: () => {
                            setEmergency(true);
                            setIsMaintenanceRequestModalVisible(true);
                        },
                        omit: Boolean(camera.thirdPartyCamera) || Boolean(camera.originalCamera) || (!authenticatedUser?.isAdmin && !authenticatedUser?.permissions.create_emergency_camera_maintenance_requests),
                    },]}
                    buttonIcon={faChevronDown}
                    buttonType={'secondary'}
                    buttonLabel='actions'
                    offsetX={-118}
                    offsetY={45}
                    size={320}
                />}
            />
            <Card style={{ flexDirection: 'row', gap: 10, ...(theaterMode ? { padding: 0, overflow: 'hidden' } : {}) }}>
                {!camera ? <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                </View> : <>
                    <View style={styles.bodyLeft}>
                        <View style={styles.bodyLeftTop} >
                            {!theaterMode &&
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                    <View style={{ flexDirection: 'row', gap: 5, minHeight: 30 }}>
                                        {camera.tags ?
                                            camera.tags.map((tag) =>
                                                <View key={tag.id} style={[styles.marker, { backgroundColor: tag.color }]}>
                                                    <MyAppText style={styles.markerText(tag.color)}>
                                                        {tag.name}
                                                    </MyAppText>
                                                </View>
                                            ) : null
                                        }
                                    </View>
                                    {camera.thirdPartyCamera ?
                                        <View>
                                            <MyAppText>{translate('integration')}: {camera.thirdPartyCamera.thirdPartyCloudProvider && camera.thirdPartyCamera.thirdPartyImageProvider ? `${camera.thirdPartyCamera.thirdPartyImageProvider.tradeName}-${camera.thirdPartyCamera.thirdPartyCloudProvider.tradeName}` : camera.thirdPartyCamera.origin}</MyAppText>
                                        </View>
                                        : null}
                                </View>
                            }
                            <View style={{ flex: 1 }}>
                                {userBearerToken &&
                                    <VideoPlayer
                                        camera={camera}
                                        watermark={watermark}
                                        ref={videoPlayerRef}
                                        onClickEvent={(playing) => {
                                            setIsPlaying(!playing);
                                        }}
                                        enableCameraInfo
                                        enableZoom
                                        userBearerToken={userBearerToken}
                                    />
                                }
                                {videoPlayer && (
                                    <VideoPlayerControls
                                        isLive={isLive}
                                        isPlaying={isPlaying}
                                        isTheaterMode={theaterMode}
                                        handleGoLive={handleGoLive}
                                        onStepBack={stepBack}
                                        onStepForward={stepForward}
                                        onTogglePlay={() => {
                                            setIsPlaying(!isPlaying);
                                        }}
                                        onPressTheaterMode={() => {
                                            setTheaterMode(!theaterMode);
                                        }}
                                        onPressFullscreen={() => {
                                            if (!videoPlayer) {
                                                return;
                                            }
                                            videoPlayer.requestFullscreen();
                                        }}
                                        onPlaybackChange={(playbackRate) => videoPlayer?.setPlaybackRate(playbackRate)}
                                    />
                                )}
                            </View>
                        </View>
                        <View >
                            {cursorTime && (
                                <TimelineWrapper
                                    cursor={cursorTime}
                                    recordList={recordList ?? { videos: [] }}
                                    onRecordClick={onRecordClick}
                                    isLoading={!recordList}
                                />
                            )}
                        </View>
                    </View>
                    {!theaterMode &&
                        <View style={styles.bodyRight}>
                            {camera.type == 'ptz' && camera.hasPtzPermission && !camera.originalCamera && !camera.thirdPartyCamera ? (
                                <Tabs tabs={[{ label: translate('ptz'), key: 'ptz' }, { label: translate('nearbyCameras'), key: 'nearbyCameras' }]}>
                                    {({ selectedTab }) => {
                                        if (selectedTab === 'nearbyCameras') {
                                            return <OtherCamerasList
                                                setCameraId={setSelectedCameraId}
                                                referenceCamera={camera}
                                            />;
                                        }
                                        if (selectedTab === 'ptz') {
                                            return <PtzControls camera={camera} />;
                                        }
                                    }}
                                </Tabs>
                            ) : (
                                <OtherCamerasList
                                    setCameraId={setSelectedCameraId}
                                    referenceCamera={camera}
                                />
                            )}
                        </View>
                    }
                </>}
            </Card>
            {camera && <>
                <DownloadModal
                    cameraId={camera.id}
                    isModalVisible={isDownloadModalVisible}
                    setModalVisible={setIsDownloadModalVisible}
                />
                <NewAnalysisModal
                    cameraId={camera.id}
                    isModalVisible={isZeusModalVisible}
                    setModalVisible={setIsZeusModalVisible}
                />
                <MaintenanceRequestModal
                    cameraId={camera?.id}
                    isModalVisible={isMaintenanceRequestModalVisible}
                    setModalVisible={setIsMaintenanceRequestModalVisible}
                    emergency={emergency}
                />
            </>}
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    content: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        columnGap: 10,
        padding: 10
    },
    headerLeft: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    body: {
        flex: 1,
        flexDirection: 'row',
        flexGrow: 1,
        padding: 16,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
        gap: 10
    },
    bodyLeft: {
        flex: 1,
        flexGrow: 4
    },
    bodyLeftTop: {
        flex: 1,
        flexGrow: 5
    },
    bodyLeftBottom: {
        flex: 1,
    },
    bodyRight: {
        flex: 1,
        borderLeftColor: theme.colors.container.border,
        borderLeftWidth: 1,
        paddingLeft: 10
    },
    marker: {
        borderRadius: 8,
        height: 16,
        paddingLeft: 10,
        paddingRight: 10,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
    downloadButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 30,
        height: 40,
        backgroundColor: theme.colors.primaryButton.background,
        borderRadius: 2,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        justifyContent: 'center'
    },
    downloadButtonText: {
        color: theme.colors.container.background,
        fontSize: 16,
        paddingLeft: 10
    },
    buttons: {
        flexDirection: 'row',
        gap: 10
    }
}));
